import { QuestionnaireResponseItem } from '@medplum/fhirtypes'
import { AdapterOptions, FlatAnswerMutation } from 'components/Assessments/adapters/types'
import { RecertFormData } from 'components/Assessments/RecertAssessment/types/RecertFormData'
import { RecertFormField } from 'components/Assessments/RecertAssessment/types/RecertFormField'
import { RecertAssessmentQuestionnaireItem } from 'components/Assessments/RecertAssessment/types/questionnaire'
import { getAnswerValuesFromItems } from 'components/Assessments/helpers'
import { BaseSubsectionAdapter } from '../../../../../adapters/BaseSubsectionAdapter'
/**
 * Integumentary subsection adapter
 * Handles skin assessment, wounds, and nail condition (6 fields)
 */
export class IntegumentaryAdapter extends BaseSubsectionAdapter<RecertFormData> {
  extractFormData(
    flatAnswers: Record<string, QuestionnaireResponseItem>,
    _options?: AdapterOptions
  ): Partial<RecertFormData> {
    const flatAnswersArray = Object.values(flatAnswers)

    // Integumentary system assessment fields
    const skinColor = getAnswerValuesFromItems(
      flatAnswersArray,
      RecertAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_INTEGUMENTARY_SKIN_COLOR
    )?.at(0)

    const skinIssues = getAnswerValuesFromItems(
      flatAnswersArray,
      RecertAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_INTEGUMENTARY_SKIN_ISSUES
    )

    const nails = getAnswerValuesFromItems(
      flatAnswersArray,
      RecertAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_INTEGUMENTARY_NAILS
    )?.at(0)

    const identifiedWounds = getAnswerValuesFromItems(
      flatAnswersArray,
      RecertAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_INTEGUMENTARY_IDENTIFIED_WOUNDS
    )?.at(0)

    const woundNotes = getAnswerValuesFromItems(
      flatAnswersArray,
      RecertAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_INTEGUMENTARY_WOUND_NOTES
    )?.at(0)

    const systemSummary = getAnswerValuesFromItems(
      flatAnswersArray,
      RecertAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_INTEGUMENTARY_SYSTEM_SUMMARY
    )?.at(0)

    return {
      [RecertFormField.INTEGUMENTARY_SKIN_COLOR]: skinColor,
      [RecertFormField.INTEGUMENTARY_SKIN_ISSUES]: skinIssues,
      [RecertFormField.INTEGUMENTARY_NAILS]: nails,
      [RecertFormField.INTEGUMENTARY_IDENTIFIED_WOUNDS]: identifiedWounds,
      [RecertFormField.INTEGUMENTARY_WOUND_NOTES]: woundNotes,
      [RecertFormField.INTEGUMENTARY_SYSTEM_SUMMARY]: systemSummary
    }
  }

  buildFlatAnswers(): FlatAnswerMutation[] {
    const mutations: FlatAnswerMutation[] = []

    if (this.formDataFieldExists(RecertFormField.INTEGUMENTARY_SKIN_COLOR)) {
      mutations.push({
        linkId: RecertAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_INTEGUMENTARY_SKIN_COLOR,
        value: this.getFormDataField(RecertFormField.INTEGUMENTARY_SKIN_COLOR)
      })
    }

    if (this.formDataFieldExists(RecertFormField.INTEGUMENTARY_SKIN_ISSUES)) {
      mutations.push({
        linkId: RecertAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_INTEGUMENTARY_SKIN_ISSUES,
        value: this.getFormDataField(RecertFormField.INTEGUMENTARY_SKIN_ISSUES)
      })
    }

    if (this.formDataFieldExists(RecertFormField.INTEGUMENTARY_NAILS)) {
      mutations.push({
        linkId: RecertAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_INTEGUMENTARY_NAILS,
        value: this.getFormDataField(RecertFormField.INTEGUMENTARY_NAILS)
      })
    }

    if (this.formDataFieldExists(RecertFormField.INTEGUMENTARY_IDENTIFIED_WOUNDS)) {
      mutations.push({
        linkId:
          RecertAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_INTEGUMENTARY_IDENTIFIED_WOUNDS,
        value: this.getFormDataField(RecertFormField.INTEGUMENTARY_IDENTIFIED_WOUNDS)
      })
    }

    if (this.formDataFieldExists(RecertFormField.INTEGUMENTARY_WOUND_NOTES)) {
      mutations.push({
        linkId: RecertAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_INTEGUMENTARY_WOUND_NOTES,
        value: this.getFormDataField(RecertFormField.INTEGUMENTARY_WOUND_NOTES)
      })
    }

    if (this.formDataFieldExists(RecertFormField.INTEGUMENTARY_SYSTEM_SUMMARY)) {
      mutations.push({
        linkId: RecertAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_INTEGUMENTARY_SYSTEM_SUMMARY,
        value: this.getFormDataField(RecertFormField.INTEGUMENTARY_SYSTEM_SUMMARY)
      })
    }

    return mutations
  }
}
